import { create } from 'zustand';
// rooms.js imports nothing, so pulling it in here cannot cycle back — worth
// checking rather than assuming, since the store is imported by almost
// everything and a cycle through it fails as an undefined at module scope.
import { ROOMS } from '../rooms';

// ?newguest=1 — wipe the guest ledger BEFORE the store reads it below. The
// ledger persists in localStorage on purpose (a real guest's progress must
// survive a reload), which means a TEST pass silently stamps the browser:
// Khari's phone "never rang" because an earlier run had already marked the
// call taken, and the world was correctly resuming a story he didn't know
// he'd started. This is the clean-slate switch for simulating a first visit.
if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('newguest')) {
  try {
    localStorage.removeItem('world:guestFields');
    localStorage.removeItem('world:guestProgress');
    sessionStorage.removeItem('world:tokenDropped');
  } catch { /* private mode */ }
  console.log('[guest] ?newguest=1 — guest ledger cleared; this visit simulates a first arrival');
}
import { meets } from '../services/access';
// worldObjects imports only config, so this cannot cycle back through the store.
import { camBasis } from '../services/worldObjects';

// World zoom levels — the descent IS the LOD strategy (see architecture v0.2 §4).
export const ZOOM = {
  ORBIT: 'orbit',
  DISTRICT: 'district',
  STREET: 'street',
  INTERIOR: 'interior'
};

// Are two venue-anchor maps the same ANSWER, regardless of being different
// objects? { key: [x, y, z] } either way, compared on value.
//
// Exists because the producer is an effect that re-derives the whole map — so
// "did anything actually move" is a question only the store can answer, and
// everything downstream is keyed on the reference it hands out.
function sameAnchorMap(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => {
    const x = a[k]; const y = b[k];
    if (x === y) return true;
    if (!x || !y || x.length !== y.length) return false;
    // Tolerance rather than equality: these come out of float matrix maths, so
    // a re-derivation can land a millionth of a unit away from the last one.
    return x.every((v, i) => Math.abs(v - y[i]) < 1e-6);
  });
}

// Car size: tuned value baked here; ?carscale= or a saved localStorage value
// override it (in that precedence) so it can be re-tuned live in ?cars=edit.
export const CAR_SCALE_DEFAULT = 0.42;
function readCarScale() {
  try {
    const q = parseFloat(new URLSearchParams(window.location.search).get('carscale'));
    if (Number.isFinite(q) && q > 0) return q;
    const ls = parseFloat(localStorage.getItem('carscale'));
    if (Number.isFinite(ls) && ls > 0) return ls;
  } catch { /* SSR / private mode */ }
  return CAR_SCALE_DEFAULT;
}

export const useWorldStore = create((set, get) => ({
  zoom: ZOOM.ORBIT,
  setZoom: (zoom) => set({ zoom }),

  // Which interior we're inside (null when outdoors) — a key of ROOMS (src/rooms.js)
  interior: null, // 'studio' | 'listening-room' | 'concert-hall'
  // `streetArrival` is cleared here, not just set on the way out. It is a
  // ONE-SHOT: it describes where this particular exit puts you, and leaving it
  // behind means the next time you step out of any shop in the city you are
  // teleported to whichever station you last surfaced at.
  // ── WHO GETS IN ──────────────────────────────────────────────────────────
  // The viewer's rung on the access ladder (services/access.js), resolved once
  // at boot and again whenever they change who they are appearing as.
  //
  // `null` means NOT YET KNOWN, which is deliberately distinct from 'everyone'.
  // A door asked about before the answer arrives must not refuse — a gate that
  // is briefly wrong on every cold load reads as broken, and the fix for a slow
  // answer is to wait for it, not to guess the strict end of it.
  accessLevel: null,
  setAccessLevel: (accessLevel) => set({ accessLevel }),
  // { ride, look, open } | null. Null = not asked yet or unreadable; the door
  // treats that as open, so a bad minute on the API never walls the studio.
  storyGate: null,
  setStoryGate: (storyGate) => set({ storyGate }),

  // WHY A DOOR SAID NO, for whoever is drawing the refusal. Holds the room, what
  // it needed, and what the viewer had — the guard turns that into a line, and
  // separating them is what stops the rule and the performance drifting apart.
  refusal: null,
  clearRefusal: () => set({ refusal: null }),

  // ONE CHOKE POINT, and this is the entire reason the check lives here.
  //
  // Six different things can open a venue: a portal trigger volume, a venue
  // marker, the street storefront, the massing block, the map flythrough, and
  // fast-travel to an avatar. Gating the door the guard is standing next to
  // would leave five ways past him, and every one of them would look like a
  // deliberate secret entrance rather than an oversight.
  //
  // Refusal is not silent. It sets `refusal` and stays outside, so something can
  // say so; returning false to a caller that ignores it is how a door ends up
  // doing nothing at all with no explanation.
  enterInterior: (interior) => {
    const s = get();
    const needs = ROOMS[interior]?.requires || null;
    if (needs && s.accessLevel && !meets(s.accessLevel, needs)) {
      set({ refusal: { room: interior, needs, has: s.accessLevel, at: Date.now() }, pendingEnter: null });
      console.log(`[access] "${interior}" needs "${needs}", viewer is "${s.accessLevel}" — refused`);
      return false;
    }
    // THE SECOND DOORMAN — story, not entitlement (Khari, 2026-08-03: no studio
    // until Spor1 has driven you and the salon has made you presentable). Same
    // shape as the rung check above: the rule is on the room, the answer came
    // from the server, and an UNKNOWN answer (null) opens the door rather than
    // inventing a refusal we cannot justify.
    if (ROOMS[interior]?.requiresStory && s.storyGate && !s.storyGate.open) {
      set({ refusal: { room: interior, story: s.storyGate, at: Date.now() }, pendingEnter: null });
      console.log(`[access] "${interior}" needs the intro (ride=${s.storyGate.ride}, look=${s.storyGate.look}) — refused`);
      return false;
    }
    set({ interior, zoom: ZOOM.INTERIOR, spawnOverride: null, pendingEnter: null, streetArrival: null, marks: [], refusal: null });
    return true;
  },
  // Leaving a venue returns you to ITS storefront on the street, not a generic
  // overview — so the block feels continuous.
  exitInterior: (arrival = null) => set((s) => ({
    interior: null, zoom: ZOOM.STREET, spawnOverride: null, streetFocus: s.interior, pendingEnter: null,
    streetArrival: arrival,
  })),

  // WHERE YOU COME OUT, when it is not where you went in.
  //
  // A shop returns you to its own storefront, which is why `streetFocus` was
  // enough: in and out are the same door. Transit breaks that — you enter the
  // underground at one station and surface at another, and the whole point is
  // that it is somewhere else. This carries the arrival pose for that case and is
  // null for every ordinary venue, which keeps the storefront behaviour exactly
  // as it was.
  streetArrival: null,
  setStreetArrival: (streetArrival) => set({ streetArrival }),

  // ── MARKS: "this one, here" ──────────────────────────────────────────────
  // What the world uses to point at an object in the room you are standing in.
  // A stylist telling you to sit, a quest naming a thing to pick up, a tutorial
  // saying look at that — all the same primitive: name it, make it obvious.
  //
  //   setMarks([{ target: 'wash_chair_1' }])
  //
  // Entries are objects rather than bare names because of what they are about to
  // carry: an action button anchored to the object, a camera pose to move to,
  // and the seat affordance to put a body on. Those hang off a mark without
  // changing what a mark is.
  //
  // Cleared on room change — a mark names an object in ONE room, and pointing at
  // something that is no longer loaded is a warning nobody can act on.
  marks: [],
  setMarks: (marks) => set({ marks: marks || [] }),

  // ── THE PLAYER IS SEATED ─────────────────────────────────────────────────
  // Deliberately "the player is seated at this pose", NOT "the camera moved".
  //
  // The camera is how that pose is RENDERED today, because first person has no
  // body yet. When it does, the body becomes a second subscriber to this value
  // rather than a rewrite of the thing that moves it — and multiplayer peers get
  // it for free, since a peer needs to know you are sitting regardless of where
  // your camera is pointing.
  //
  //   seated = { target, phase, seat: { pos, yaw, eyeY, pitch }, from: { pos, yaw, pitch } }
  //   phase   'sitting' → 'seated' → 'standing' → null
  //
  // `from` is captured on the way in so standing up returns you where you were,
  // which is what makes it an action rather than a teleport.
  seated: null,
  sitAt: (pose) => set({ seated: { ...pose, phase: 'sitting' } }), // eslint-disable-line
  seatSettled: () => set((s) => (s.seated ? { seated: { ...s.seated, phase: 'seated' } } : s)),
  standUp: () => set((s) => (s.seated ? { seated: { ...s.seated, phase: 'standing' } } : s)),
  standCleared: () => set({ seated: null }),

  // ── FREECAM: off the floor, on purpose ───────────────────────────────────
  // Q/E to rise and fall inside a room, with FloorClamp standing down while it
  // is on.
  //
  // It is an AUTHORING tool, and it also fixes a real hole: FloorClamp can only
  // ever put you DOWN onto something. Arrive below the floor — a spawn set too
  // low, a fly-in that overshoots, a model grounded on its track bed rather than
  // its platform — and the downward ray finds nothing but empty space, holds you
  // still, and there is no input in the game that can lift you back up. You are
  // stuck under the world with a working camera.
  //
  // Both mechanisms cannot own the camera's height at once, which is why this is
  // a flag both of them read rather than a second thing that moves it.
  // ── VR: the phone as a viewport ──────────────────────────────────────────
  // `gyro` — device orientation drives the camera's rotation (see GyroLook).
  // `stereo` — side-by-side output for a Cardboard-style holder.
  //
  // Two flags rather than one because they are independently useful: gyro alone
  // is a magic window you hold up and turn with, which is worth having on its
  // own and is the only half that works without a headset.
  //
  // Flags live in the store; the orientation itself does NOT — it changes at
  // device rate and would re-render the tree on every head movement.
  gyro: false,
  setGyro: (gyro) => set({ gyro, ...(gyro ? {} : { stereo: false }) }),
  stereo: false,
  setStereo: (stereo) => set({ stereo }),

  // ── LENS CENTRE OFFSET (the big-phone doubling fix) ──────────────────────
  // Splitting the canvas down the middle puts each eye's image centre at the
  // centre of ITS HALF. The eye does not look at the centre of its half — it
  // looks through a lens, and on a fixed holder those lenses are ~64mm apart
  // regardless of how wide the phone is.
  //
  // On a 6.7" phone the half is ~77mm, so each half-centre sits ~6.6mm OUTBOARD
  // of its lens. Fusing then requires the eyes to DIVERGE, which they cannot do
  // — so the pair reads as two pictures rather than one with depth. It presents
  // exactly like an eye-separation problem and is not one: `eyeSep` is the
  // virtual camera baseline and should stay at the real IPD. Raising it to
  // chase this adds divergence and makes it worse.
  //
  // Stored in CSS px, applied by StereoRender as a viewport shift INWARD.
  // Persisted because it is a property of a particular phone in a particular
  // holder — found once, then true forever, and a reload should not lose it.
  eyeShift: (() => {
    try {
      const qs = new URLSearchParams(window.location.search).get('eyeshift');
      if (qs !== null && Number.isFinite(parseFloat(qs))) return parseFloat(qs);
      const saved = parseFloat(localStorage.getItem('vr:eyeshift'));
      return Number.isFinite(saved) ? saved : 0;
    } catch { return 0; }
  })(),
  setEyeShift: (px) => {
    const eyeShift = Number.isFinite(px) ? Math.round(px * 10) / 10 : 0;
    try { localStorage.setItem('vr:eyeshift', String(eyeShift)); } catch { /* private mode */ }
    set({ eyeShift });
    return eyeShift;
  },

  // Per-second readout of distance actually kept, the floor probe's hit rate and
  // the frame rate — the three numbers that separate "something is stealing my
  // movement" from "this room looks bigger than it is".
  speedMeter: false,
  toggleSpeedMeter: () => set((s) => ({ speedMeter: !s.speedMeter })),

  freeCam: false,
  setFreeCam: (freeCam) => set({ freeCam }),
  toggleFreeCam: () => set((s) => ({ freeCam: !s.freeCam })),

  // ── STAGING: where a character should be STANDING ────────────────────────
  // A mark points at an object. This moves a person. Both are direction, and
  // they are separate because the room can only answer one of them: the chair's
  // position is IN the model, but "the stylist comes over to you" is a beat
  // somebody wrote.
  //
  //   staging = { sassy_la: { pos: [x,y,z], rotY, mark: 'chair' } | null }
  //
  // KEYED BY SLUG, and a MARK NAME rather than a destination, because the room
  // owns the floor plan (ROOMS.<id>.stage) and the scene owns the cue. A scene
  // that hardcoded coordinates would have to be rewritten every time a desk
  // moved; this one says "go to the chair" and the room says where that is.
  //
  // No entry = go home, where home is wherever the resident was placed. The walk
  // back is not a special case: the body eases toward whatever its current
  // target is, so removing a stage direction IS the instruction to return.
  staging: {},
  stageTo: (slug, mark) => set((s) => ({ staging: { ...s.staging, [slug]: mark || null } })),
  clearStaging: () => set({ staging: {} }),

  // ── THE ENSEMBLE: the conversation a room is having without you ───────────
  //
  // Staging above moves a body somewhere. This is the other half — who is
  // TALKING, and who they are talking to — and it is deliberately the smallest
  // possible piece of state: ONE line, the one being spoken now.
  //
  //   banter = { actor, to, text, name } | null
  //
  // Keyed by ACTOR KEY, not by model slug, because the whole point of the actor
  // layer is that the barber is the barber whichever body is playing him
  // (migration 281). InteriorResidents maps actor → the placement carrying it.
  //
  // A queue would be the obvious shape and would be wrong: the timeline lives in
  // EnsembleDirector, which owns one chain of timers and one audio element. Two
  // things holding a schedule is how a scene ends up half-spoken twice.
  banter: null,
  sayBanter: (banter) => set({ banter }),
  // The cast list, for anything that wants to know who is in the room without
  // re-reading placements (nameplates, the admin panel's sanity check).
  ensemble: null,                    // { zone, cast: [...], sceneKey } | null
  setEnsemble: (ensemble) => set({ ensemble }),

  // ── THE RECORD SHOP ──────────────────────────────────────────────────────
  //
  // Khari, 2026-08-04: "As he makes suggestions, they will show on the wall
  // where the records are at."
  //
  // The wall was already a list sliced to however many sleeves are placed, so
  // the clerk does not need a rendering path of his own — he needs to be able to
  // change the LIST. That is all this is.
  //
  // null is NOT the empty shelf: null means "nobody has recommended anything,
  // show the featured records", and [] would mean "he took everything down".
  // The shop is never bare because a conversation has not started yet.
  shelfTracks: null,                 // [track] | null
  setShelfTracks: (shelfTracks) => set({ shelfTracks }),
  // Who you are talking to at the counter. Separate from `chatAvatar` (free-form
  // artist chat) and `scene` (a directed scene) because this one is a SHOP: it
  // owns the wall, and closing it puts the featured records back.
  shop: null,                        // { actorKey, name, role } | null
  openShop: (shop) => set({ shop }),
  closeShop: () => set({ shop: null, shelfTracks: null }),

  // ── PERSON-TO-PERSON CALLS ───────────────────────────────────────────────
  //
  // Khari, 2026-08-04: "If I call someone, it will send them a push
  // notification on their phone.. they get the notification with a link to
  // answer the call.. when they answer, the peer to peer begins (starts with
  // the option to appear as character, with no streaming until you are ready)."
  //
  // TWO pieces of state, and the split matters:
  //
  //   incomingCall  somebody is RINGING you. Arrives over the world socket
  //                 (instant, if you are in the city) or from ?call= on a link
  //                 followed from a notification. Owns nothing but the ring.
  //   call          the call you are IN or setting up — the phone's own screen
  //                 state. Answering moves one to the other.
  //
  // A ring is not a call: you can be rung while walking, ignore it, and have it
  // expire, and none of that should touch a session or a camera.
  //
  // NAMED `video*` BECAUSE `incomingCall`/`ring`/`call` ARE ALREADY TAKEN by the
  // scripted NPC calls further down this file (Lyra ringing you with a quest).
  // Two kinds of call, two vocabularies: one is a scene the game places, the
  // other is a person dialling a person. Reusing the keys would have shadowed
  // the quest call — an object literal keeps the LAST definition — and the
  // symptom would have been the intro silently losing its phone.
  videoRing: null,                   // { code, from: { name }, asAvatarId } | null
  ringVideo: (videoRing) => set({ videoRing }),
  dismissVideoRing: () => set({ videoRing: null }),
  // { code, peerName, answering } — set when the phone opens a video-call screen.
  videoCall: null,
  openVideoCall: (videoCall) => set({ videoCall, videoRing: null, phoneOpen: true, phoneApp: 'video' }),
  closeVideoCall: () => set({ videoCall: null }),

  // Map-nav flythrough: pick a venue → fly the street camera there → once it
  // settles, auto-enter. pendingEnter holds the venue we're flying to enter.
  pendingEnter: null,
  setPendingEnter: (pendingEnter) => set({ pendingEnter }),

  // Designed spawn shipped by a room's splat manifest ({ pos, look }) — takes
  // precedence over the ROOMS default once the splat loads.
  spawnOverride: null,
  setSpawnOverride: (spawnOverride) => set({ spawnOverride }),

  // A world-space landmark reported by the loaded city model (e.g. the parked
  // car) — the model fly-in lands beside it. [x,y,z] or null.
  landmark: null,
  setLandmark: (landmark) => set({ landmark }),

  // Venue anchors inside the loaded city model: { <venueKey>: [x,y,z] } — the
  // real building each venue sits on, computed after the model loads.
  venueAnchors: null,
  // VALUE-STABLE. CityModel rebuilds this map whenever it re-derives anchors,
  // and a fresh object with identical numbers is still a new reference — which
  // re-runs every memo and effect keyed on it right down the tree. That is
  // invisible until something downstream logs or does real work, and then it
  // shows up as the same line printed a hundred times over.
  //
  // Publishing only on a real CHANGE means the recompute can happen as often as
  // it likes and costs nothing when the answer is the same.
  setVenueAnchors: (venueAnchors) => set((s) => (
    sameAnchorMap(s.venueAnchors, venueAnchors) ? s : { venueAnchors }
  )),

  // The city model's own fitted footprint ({ min:[x,z], max:[x,z], top }) —
  // measured after the fit transform, so the enclosing walls and the walk clamp
  // sit against the REAL edge of the model instead of a radius someone guessed.
  cityBounds: null,
  setCityBounds: (cityBounds) => set({ cityBounds }),
  // The fit CityModel applies to its rendered copy ({ scale, position }).
  // CityModel renders scene.clone(true), so the gltf scene every other component
  // reads from useGLTF is NOT in the render tree and its world matrices are all
  // identity. Anything cloning a mesh out of it therefore gets raw model scale —
  // ~30x too big, thousands of units away — unless it applies this itself.
  cityFit: null,
  setCityFit: (cityFit) => set({ cityFit }),
  // How far the skyline's outermost corner reaches. The shell has to contain
  // it: sizing the shell off the city alone left the towers poking through,
  // because they stand well outside the city and are 45 units deep themselves.
  skylineOuter: 0,
  setSkylineOuter: (skylineOuter) => set({ skylineOuter }),

  // Bounding box of each venue's building: { <venueKey>: { center:[x,y,z],
  // size:[w,h,d] } } — used to make the whole building clickable + hover-glow.
  venueBoxes: null,
  setVenueBoxes: (venueBoxes) => set({ venueBoxes }),

  // MEASURED SIZE OF A VENUE'S INTERIOR MODEL, as built — { <roomId>: [w,h,d] }
  // in world units, after any `fitTo` scaling. What the plan view draws the
  // shop's own footprint from, so you can see it against the building it stands
  // in and judge whether it fits.
  //
  // CACHED IN localStorage, because the only thing that can measure it is
  // MeshInterior, and MeshInterior only exists while you are standing inside the
  // room. Without a cache the plan view could never show you a venue you were
  // not already in — which is exactly backwards, since the reason to look at the
  // plan is to decide where to put it. Recorded on the first visit, drawn
  // forever after. A stale entry after a model is re-exported self-corrects the
  // next time anyone walks in.
  venueFootprints: (() => {
    try { return JSON.parse(localStorage.getItem('world:venueFootprints') || '{}'); }
    catch { return {}; }
  })(),
  setVenueFootprint: (roomId, size) => set((s) => {
    const prev = s.venueFootprints[roomId];
    // Same numbers → same object, so this never re-renders the overlay on a
    // re-entry into a room that has not changed.
    if (prev && prev.every((v, i) => Math.abs(v - size[i]) < 0.01)) return s;
    const venueFootprints = { ...s.venueFootprints, [roomId]: size };
    try { localStorage.setItem('world:venueFootprints', JSON.stringify(venueFootprints)); }
    catch { /* private mode — the overlay just falls back to "not measured" */ }
    return { venueFootprints };
  }),

  // ── Venue anchor editing (AdminPanel → "Venue anchors", best in build view) ──
  //
  // Where a venue STANDS, moved by hand instead of by the sampler. Saved as
  // world_objects rows (kind 'venue_anchor', ref = the room id), which needs no
  // migration and no API change — `kind` is a free column — and means
  // repositioning a venue is a save rather than an edit to rooms.js and a
  // redeploy. Same contract the rest of this editor already works to.
  //
  // An override beats both the rooms.js `anchorMesh` pin and the sampler;
  // CityModel merges it in so every existing consumer of `venueAnchors` —
  // markers, spawn, MeshInterior — needs no changes at all.
  // { [roomId]: { x, z, scale, rotY } } — position, size AND facing, because on
  // a sited venue they are one decision. "Does it fit here" has a different
  // answer depending on how big it is and which way it is turned, so tuning them
  // apart means guessing at two while judging the third. All three ride columns
  // the row already has (`pos_*`, `size`, `rot_y`); no schema change.
  venueAnchorOverrides: {},
  setVenueAnchorOverrides: (venueAnchorOverrides) => set({ venueAnchorOverrides }),

  // THE SHELL'S OWN TRIM — { [roomId]: { y, scale } }.
  //
  // A venue is two models fitted by two components, and the last few centimetres
  // between them will never come out of a rule: they are different geometry with
  // different origins. So the exterior gets its own height and size, saved
  // separately, and the venue tool edits whichever one you are looking at —
  // inside the room it adjusts the room, outside it adjusts the shell.
  //
  // A second world_objects row (kind 'venue_shell') rather than more columns on
  // the first. Same free-varchar trick as venue_anchor: no migration, and the
  // two are independently resettable, which matters because getting the shell
  // wrong should never mean redoing the room.
  venueShellOverrides: {},
  setVenueShellOverrides: (venueShellOverrides) => set({ venueShellOverrides }),
  // Which venue the footprint brush is currently carrying (a room id, or null).
  venuePlaceRoom: null,
  setVenuePlaceRoom: (venuePlaceRoom) => set((s) => ({
    venuePlaceRoom: s.venuePlaceRoom === venuePlaceRoom ? null : venuePlaceRoom,
  })),
  // Live cursor readout: { x, z, mesh, meshSize } — what is under the pointer
  // right now, so the panel can say whether the drop will work before the click.
  venuePlaceHover: null,
  setVenuePlaceHover: (venuePlaceHover) => set({ venuePlaceHover }),
  // Unsaved moves, { [roomId]: { x, z, scale } }. Placing is a click and most of
  // the first few are wrong, so nothing is committed until Save — exactly like
  // the building grid.
  //
  // A PATCH, not a replacement: dropping the outline sets x/z and dragging the
  // slider sets scale, and neither should discard the other. Seeded from the
  // saved override so nudging the slider on an already-placed venue starts from
  // where it actually is rather than from 1.
  // Carries BOTH sets: x/y/z/scale/rotY for the venue, shellY/shellScale for the
  // exterior's trim. One draft rather than two, so Save is one action and the
  // "unsaved" dot means the same thing wherever you were editing.
  venuePlaceDraft: {},
  setVenuePlaceDraft: (roomId, patch) => set((s) => ({
    venuePlaceDraft: {
      ...s.venuePlaceDraft,
      [roomId]: {
        ...(s.venuePlaceDraft[roomId]
          || { ...(s.venueAnchorOverrides[roomId] || {}), ...(s.venueShellOverrides[roomId] ? { shellY: s.venueShellOverrides[roomId].y, shellScale: s.venueShellOverrides[roomId].scale } : {}) }),
        ...patch,
      },
    },
  })),
  clearVenuePlaceDraft: () => set({ venuePlaceDraft: {} }),

  // WHICH HALF THE PANEL IS EDITING. 'auto' follows where you are standing —
  // inside the venue it edits the room, outside it edits the shell — which is
  // the whole point: the control should act on the thing you can see. Pinning it
  // is for the case where you want to nudge the shell from the doorway.
  venueEditTarget: 'auto',
  setVenueEditTarget: (venueEditTarget) => set({ venueEditTarget }),

  // A one-shot walk teleport target ({ pos:[x,y,z], look:[x,y,z] }) — quick
  // travel while walking. WalkControls consumes it and clears it.
  walkTarget: null,
  setWalkTarget: (walkTarget) => set({ walkTarget }),

  // Debug mesh picker (?pick=1): the last mesh clicked in the city model, so we
  // can identify a specific sign to swap dynamic graphics onto.
  picked: null,
  setPicked: (picked) => set({ picked }),

  // Which town location the STREET camera is framing (a TOWN_LOCATIONS key, or
  // null for the establishing block overview). The HUD minimap sets this to fly
  // the camera to a venue; entering a location goes through enterInterior.
  streetFocus: null,
  setStreetFocus: (streetFocus) => set({ streetFocus, zoom: ZOOM.STREET, interior: null }),

  // Walkable bounds of the current splat room ({min:[x,y,z], max:[x,y,z]}),
  // measured from the splat's own extents on load. The camera is clamped
  // inside them so visitors never see the room's fuzzy outer shell.
  roomBounds: null,
  setRoomBounds: (roomBounds) => set({ roomBounds }),
  boundsEnabled: true,
  setBoundsEnabled: (boundsEnabled) => set({ boundsEnabled }),

  // Bumped when the camera panel saves/resets a room's tuned config so the
  // scene re-reads the localStorage override.
  camVersion: 0,
  bumpCamVersion: () => set((s) => ({ camVersion: s.camVersion + 1 })),

  // False while the camera rig is flying between zoom levels. Orbit
  // constraints only engage once settled — clamping a mid-flight camera
  // yanks it off the path and entry lands somewhere random.
  camSettled: false,
  setCamSettled: (camSettled) => set({ camSettled }),

  // WHEN THE PLAYER ACTUALLY ARRIVED IN THE CITY — the moment the fly-in puts
  // their feet down, not the moment they pressed Enter.
  //
  // The story's clock hangs off this. `camSettled` cannot do the job: it flips
  // true for ANY completed camera move, including the orbit ease that runs
  // while CharacterSelect is still open. Anchoring on it meant the 15s before
  // Lyra's call was spent choosing a character (Khari, 2026-08-06: "when the
  // fly-in happens, taking me into town, the phone is already ringing"), so
  // she rang over the descent instead of after it.
  //
  // Set ONCE per session and never reset — arriving is not something you do
  // twice, and a re-latch would restart every timer that hangs off it.
  arrivedAt: null,
  markArrived: () => set((s) => (s.arrivedAt ? {} : { arrivedAt: Date.now() })),

  // Live Anam video-chat stream (an HTMLVideoElement carrying the WebRTC
  // feed) — when set, the room's VideoPanel swaps its recorded cameo for
  // this live texture. Owned by LiveChatDock (DOM side).
  liveVideoEl: null,
  setLiveVideoEl: (liveVideoEl) => set({ liveVideoEl }),

  // Voice chat with a 3D resident (e.g. Rebel on the street). chatAvatar holds
  // { avatarId, name } while a conversation is open; AvatarChatDock (DOM side)
  // drives the push-to-talk turn loop. Clicking the character opens it.
  chatAvatar: null,
  openChat: (chatAvatar) => set({ chatAvatar }),
  closeChat: () => set({ chatAvatar: null }),

  // A DIRECTED conversation (Stage One acts) — an NPC with an agenda, driven by
  // /api/dialogue. Distinct from chatAvatar, which is free-form chat with an
  // artist: this one is a scene that starts, extracts, and ends.
  //
  // `room` names the interior a scene belongs to (null for scenes that happen
  // outdoors — a ride, a phone call). SceneDock ends a scene when you walk out
  // of ITS room, and needs to know the difference.
  scene: null,                       // { key, title, quest, room } | null
  // Opening a scene from the WORLD always releases the phone's claim on it —
  // otherwise a scene started while a call happened to be up would be painted on
  // a 260px screen instead of in the room it belongs to. `answerCall` is the one
  // path that sets the claim, and it sets `scene` itself.
  openScene: (scene) => set({ scene, phoneCall: false }),
  closeScene: () => set({ scene: null, phoneCall: false }),

  // ── The ride (Build_Game_Intro §4.2) ─────────────────────────────────────
  // While set, RideSequence owns the street camera — WalkControls stands down
  // entirely (the codebase's repeated failure mode is two mechanisms claiming
  // the camera at once, so ownership is a single flag, not a negotiation).
  // The scene itself is ordinary `scene` state above; this is only the CAR.
  ride: null,                        // { sceneKey, pace } | null
  startRide: (ride) => set({ ride, phoneOpen: false, phoneApp: null }),
  // A landmark just went past — SceneDock turns it into a parenthetical world
  // note for the dialogue, RideOverlay turns it into the reveal card. Last one
  // wins; they are moments, not a queue.
  rideNote: null,                    // { key, name, accent, line } | null
  setRideNote: (rideNote) => set({ rideNote }),
  // The salon's reserved mirror (Khari, 2026-08-03): the vanity beside the
  // wash chair always shows YOUR current look. Signed URL of the player's
  // look image — set by the salon scene when a fresh look lands, or fetched
  // from the avatar's profile on walk-in.
  salonLookUrl: null,
  setSalonLookUrl: (salonLookUrl) => set({ salonLookUrl }),
  // The scene closed → glide to the kerb. RideSequence plays the drop-off and
  // then clears everything itself; callers only ever ask.
  rideDropoff: false,
  requestDropoff: () => set({ rideDropoff: true }),
  endRide: () => set({ ride: null, rideDropoff: false, rideNote: null }),

  // ── The premiere (Build_Game_Intro §4.6) ─────────────────────────────────
  // Armed when the producer releases the record on the player's yes; FIRED by
  // PremiereMoment when they walk out of the studio — the first play happens
  // on the street, where they are standing. { trackId, title, audioUrl,
  // stations, avatarId } | null. Cleared when the moment has played.
  premiere: null,
  setPremiere: (premiere) => set({ premiere }),

  // ── The handoff (Build_Game_Intro §4.7) ──────────────────────────────────
  // Set the moment grant_autonomy fires on the call; consumed by
  // HandoffWalkaway once the call ends — she walks away into the city, THE
  // CAMERA DOES NOT FOLLOW (nothing claims it; the player keeps standing
  // where they stood), her song on the air over the moment.
  handoff: null,                     // { avatarId } | null
  setHandoff: (handoff) => set({ handoff }),

  // ── The guest start (Khari, 2026-08-02) ──────────────────────────────────
  // A cold visitor plays Act 0 and the ride with NO account: the interview's
  // answers live HERE (the server writes nothing for guests) until the salon
  // signup commits them through the real endpoints. Persisted — a reload
  // between the kerb and the chair must not cost anyone their story.
  guestFields: (() => {
    try { return JSON.parse(localStorage.getItem('world:guestFields') || '{}'); } catch { return {}; }
  })(),
  mergeGuestFields: (fields) => set((s) => {
    const next = { ...s.guestFields, ...(fields || {}) };
    try { localStorage.setItem('world:guestFields', JSON.stringify(next)); } catch { /* private mode */ }
    return { guestFields: next };
  }),
  guestProgress: (() => {
    try { return JSON.parse(localStorage.getItem('world:guestProgress') || '{}'); } catch { return {}; }
  })(),
  setGuestProgress: (patch) => set((s) => {
    const next = { ...s.guestProgress, ...(patch || {}) };
    try { localStorage.setItem('world:guestProgress', JSON.stringify(next)); } catch { /* private mode */ }
    return { guestProgress: next };
  }),
  clearGuestState: () => {
    try { localStorage.removeItem('world:guestFields'); localStorage.removeItem('world:guestProgress'); } catch { /* noop */ }
    set({ guestFields: {}, guestProgress: {} });
  },

  // ── The walk's producer choice (Build_Game_Intro §4.4) ───────────────────
  // Picked from Lyra's text, on instinct, from faces on a screen. Avatar-
  // scoped and persisted locally: a refresh between the salon door and the
  // studio must not silently re-book you with somebody else.
  producer: (() => {
    try { return JSON.parse(localStorage.getItem('comeup_producer') || 'null'); } catch { return null; }
  })(),
  setProducer: (producer) => {
    try {
      if (producer) localStorage.setItem('comeup_producer', JSON.stringify(producer));
      else localStorage.removeItem('comeup_producer');
    } catch { /* private mode — the session still holds it */ }
    set({ producer });
  },

  // ── The phone (treatment §3) ─────────────────────────────────────────────
  // The one permitted non-diegetic-looking surface in the world, and the
  // container the rest of HypeTunes migrates into. It is never modal: the world
  // keeps running behind it, so this is a raise/lower flag, not a pause.
  phoneOpen: false,
  setPhoneOpen: (phoneOpen) => set({ phoneOpen }),
  togglePhone: () => set((s) => ({ phoneOpen: !s.phoneOpen })),
  phoneApp: null,                    // null = home screen | 'calls' | 'rides' | 'quests'
  setPhoneApp: (phoneApp) => set({ phoneApp }),

  // A call is a `scene` like any other — the only difference is WHICH surface
  // paints it. `phoneCall` is that claim: while it's true the phone renders the
  // transcript and SceneDock stands down, so one conversation never runs twice.
  phoneCall: false,
  // ── LISTENING ROOM (project_world_listening_rooms) ────────────────────────
  //
  // `listeningRoom` is the SERVER's view of the room this client is watching —
  // seats, who is in them, and what is playing. It is replaced wholesale rather
  // than merged: a room is a dozen seats, so the payload is small and there is
  // no arrival order to get wrong.
  //
  // `listeningInvite` is somebody holding a chair for you right now. Kept apart
  // from `incomingCall` deliberately — being invited to sit and being rung are
  // different urgencies, and collapsing them would make one of them interrupt
  // like the other.
  listeningRoom: null,               // server publicState | null
  listeningInvite: null,             // { code, roomId, zone, from, at } | null
  setListeningRoom: (listeningRoom) => set({ listeningRoom: listeningRoom || null }),
  setListeningInvite: (listeningInvite) => set({ listeningInvite: listeningInvite || null }),
  clearListeningInvite: () => set({ listeningInvite: null }),

  /**
   * AN INVITE IS SOMEBODY TRYING TO REACH YOU, so it belongs in the Calls app.
   *
   * Khari, 2026-08-04: "it should come to my notifications, and effectively send
   * me a push notification. Also, it should appear in my phone (on screen). I
   * should see it in my calls... listed as the most recent call and the 'call
   * back' button is relabeled and opens the room, transporting me there."
   *
   * Filed as a missed call rather than a new list: the phone already badges
   * that, already keeps it after you look away, and already knows how to draw a
   * face and an action. A listening invite is a different KIND of the same
   * thing, not a different thing.
   *
   * `sceneKey` is synthetic (`listen:CODE`) — it is only ever an identity for
   * dedupe here, and the row skips the caller-face lookup for exactly that
   * reason. Re-inviting the same room replaces its row instead of stacking.
   */
  noteListeningInvite: (inv) => set((s) => {
    if (!inv?.code) return {};
    const entry = {
      kind: 'listening',
      sceneKey: `listen:${inv.code}`,
      code: inv.code,
      zone: inv.zone || null,
      from: inv.from?.name || 'Someone',
      role: 'Listening room',
      image: inv.from?.image || null,
      at: inv.at || Date.now(),
    };
    return {
      // Newest first — he asked for it at the top of the log, and it is the
      // only one of these that is actionable right now.
      missedCalls: [entry, ...s.missedCalls.filter((c) => c.sceneKey !== entry.sceneKey)],
    };
  }),

  incomingCall: null,                // { sceneKey, quest, from, role } while ringing
  ring: (incomingCall) => set({ incomingCall }),
  answerCall: (call) => set((s) => {
    // Only accept something that actually looks like a call. A handler wired
    // straight to onClick would otherwise hand this a PointerEvent, and the
    // scene would open with an undefined key ("sceneKey is required").
    const c = (call && typeof call.sceneKey === 'string') ? call : s.incomingCall;
    if (!c?.sceneKey) return {};
    return {
      scene: {
        key: c.sceneKey, title: c.from, quest: c.quest || null, room: null,
        // Carried so the call screen can close out a `talk_to <giver>` objective
        // without knowing which act it is running.
        giverRef: c.giverRef || null, role: c.role || null,
      },
      phoneCall: true,
      phoneOpen: true,
      phoneApp: 'calls',
      incomingCall: null,
      missedCalls: s.missedCalls.filter((m) => m.sceneKey !== c.sceneKey),
    };
  }),
  // Never trap the player (design law 2): a declined call is a MISSED call, not
  // a closed door — the Calls app lists it and it can be returned any time.
  // PUT THE PHONE AWAY TOO. It came out of your pocket because it rang
  // (<Phone/> opens it on ring), so declining has to undo the whole gesture —
  // otherwise you decline a call and are left holding a phone you never took
  // out, needing a second press to put it back. One action, one reversal.
  //
  // The call is not lost: it lands in missedCalls and the Calls app can return
  // it, which is the rule that lets declining be this decisive.
  declineCall: () => set((s) => (s.incomingCall ? {
    incomingCall: null,
    missedCalls: [...s.missedCalls.filter((c) => c.sceneKey !== s.incomingCall.sceneKey), s.incomingCall],
    phoneOpen: false,
  } : {})),
  missedCalls: [],
  // The quest chain, published by <Phone/> (which is always mounted) so the
  // SCREEN can read it from either placement — HUD or in-hand — without each
  // one fetching its own copy and disagreeing about what's unlocked.
  phoneQuests: [],
  setPhoneQuests: (phoneQuests) => set({ phoneQuests: phoneQuests || [] }),
  refreshQuests: () => {},
  setRefreshQuests: (refreshQuests) => set({ refreshQuests }),

  // Which point of view the player is in. The ROOM decides by default
  // (rooms.js `firstPerson`); this override is the escape hatch — ?pov=first on
  // the street, or a deliberate switch — so there is still only one source of
  // truth to change when a room's framing changes.
  povOverride: (() => {
    try {
      const v = new URLSearchParams(window.location.search).get('pov');
      return v === 'first' || v === 'third' ? v : null;
    } catch { return null; }
  })(),
  setPovOverride: (povOverride) => set({ povOverride }),

  clearMissedCall: (sceneKey) => set((s) => ({ missedCalls: s.missedCalls.filter((c) => c.sceneKey !== sceneKey) })),
  // True while that resident is actively speaking — AnimatedResident crossfades
  // walk→idle and layers a talking head-gesture (per-frame amplitude arrives via
  // talkBridge, not the store, to avoid re-rendering the scene each frame).
  residentTalking: false,
  setResidentTalking: (residentTalking) => set({ residentTalking }),

  // The viewer has passed the loading→enter gate (Loader). Flips on the Enter
  // click (a user gesture → audio can start), which also kicks the fly-in.
  entered: false,
  setEntered: (entered) => set({ entered }),

  // Broadcast layer (the district's live station) vs pulse layer (ambient soundscape)
  musicOn: true,
  toggleMusic: () => set((s) => ({ musicOn: !s.musicOn })),

  // Live now-playing state from the district's station [reuse /api/radio/stations/:slug/now]
  nowPlaying: null,
  setNowPlaying: (nowPlaying) => set({ nowPlaying }),

  // Compact per-track modulation track: beat + sentiment driving the world's mood
  // (architecture v0.2 §5). Derived from existing track analysis.
  modulation: null,
  setModulation: (modulation) => set({ modulation }),

  // Venue presence: other listeners as felt figures [reuse listening-room presence]
  presences: [], // [{ userId, handle }]
  setPresences: (presences) => set({ presences }),
  online: 0,     // total participants connected to /world (from world_state)
  setOnline: (online) => set({ online }),
  embodied: [],  // avatar_ids currently PLAYER-DRIVEN — hide their autonomous NPC
  setEmbodied: (embodied) => set({ embodied: Array.isArray(embodied) ? embodied : [] }),
  myAvatarId: null, // the avatar THIS member is embodying (server-resolved; drives spawn)
  setMyAvatarId: (myAvatarId) => set({ myAvatarId: myAvatarId || null }),
  chat: [],      // recent proximity messages [{ id, handle, text, ts, me }]
  pushChat: (m) => set((s) => ({ chat: [...s.chat.slice(-39), m] })),
  micVisible: false, // the center quick-speak mic is showing → raise WASD above it
  setMicVisible: (micVisible) => set({ micVisible: !!micVisible }),

  // ── Ad/promo PLACEMENTS (display-surface inventory) ──────────────────────
  placements: [],                    // saved surfaces from /api/world/placements
  setPlacements: (placements) => set({ placements: placements || [] }),
  upsertPlacement: (p) => set((s) => ({
    placements: s.placements.some((x) => x.placement_id === p.placement_id)
      ? s.placements.map((x) => (x.placement_id === p.placement_id ? p : x))
      : [...s.placements, p],
  })),
  removePlacement: (id) => set((s) => ({ placements: s.placements.filter((p) => p.placement_id !== id) })),
  // The in-world tool's current draft settings (what the ghost quad will become).
  // pos/quat are null until a surface is SELECTED, then they hold its live
  // transform so the nudge controls have something to edit. Re-placing at the
  // crosshair only works from a good angle — flat surfaces like water are nearly
  // impossible to aim at obliquely — so a placed surface also has to be movable
  // by increments, without re-raycasting.
  placementDraft: { aspect: '16:9', surface_type: 'billboard', frame_style: 'framed', traffic_tier: 'main', width: 4, label: '', content_policy: [], playback: 'rotate', trigger_mode: 'proximity', volume: 1, trigger_radius: 8, default_content_ref: '', pos: null, quat: null },
  setPlacementDraft: (patch) => set((s) => ({ placementDraft: { ...s.placementDraft, ...patch } })),
  // A billboard the VISITOR opened (not admin edit) — shows the artist's tracks.
  activeBillboard: null,
  setActiveBillboard: (activeBillboard) => set({ activeBillboard }),
  // THE DROP. Tracks that finished while you were somewhere else — the phone is
  // the only thing still watching once the session ends, so it carries the news.
  recordAlerts: [],                  // trackIds that landed unseen
  pushRecordAlert: (id) => set((s) => (s.recordAlerts.includes(id) ? {} : { recordAlerts: [...s.recordAlerts, id] })),
  markRecordsSeen: () => set({ recordAlerts: [] }),

  // Record-wall editor state, lifted so the ⚙ panel and the 3D frames agree on
  // which one is selected and where they all are (the same pattern the routes
  // and NPC editors use).
  // Two groups, because the studio has two surfaces: 'wall' (released records,
  // behind you) and 'results' (what just came out of the room, in front of you).
  plaqueEdit: { group: 'wall', i: 0 },
  setPlaqueEdit: (plaqueEdit) => set({ plaqueEdit }),
  plaqueXf: { wall: [], results: [] },   // live transforms, published by each group
  setPlaqueXf: (group, list) => set((s) => ({ plaqueXf: { ...s.plaqueXf, [group]: list } })),
  // `n` makes every nudge a NEW object even when the patch repeats, so holding
  // a button keeps moving instead of stopping on an identical value.
  plaqueNudge: null,
  nudgePlaque: (group, i, patch) => set((s) => ({ plaqueNudge: { group, i, patch, n: (s.plaqueNudge?.n || 0) + 1 } })),

  // A RECORD PLAQUE the owner opened — a `my_record` placement showing one of
  // their own tracks. Opens a DOM panel rather than the in-scene grid, because
  // it carries actions (artwork, release) that want real buttons.
  activePlaque: null,                // { group, i, source } | null
  setActivePlaque: (activePlaque) => set({ activePlaque }),
  selectedPlacement: null,           // placement_id being edited (click to select)
  setSelectedPlacement: (selectedPlacement) => set({ selectedPlacement }),

  // Routes editor (?routes=edit) selection — lifted so the on-screen RoutesPanel,
  // the clickable 3D node handles, and the keyboard all target the same node.
  routeEdit: { routeIdx: 0, ptIdx: 0 },
  setRouteEdit: (patch) => set((s) => ({ routeEdit: { ...s.routeEdit, ...patch } })),

  // NPC placement editor (?place=edit): the loaded roster (for the panel list)
  // and which NPC is selected for moving. Position overrides persist per-slug in
  // localStorage (npcpos:<slug>); copy the logged post line back into roster.json.
  npcList: [],
  setNpcList: (npcList) => set({ npcList }),
  // Hand a driven avatar's LAST live position to its NPC when the driver stops
  // (so the un-hidden NPC stays where the marker was, not its stale load spot).
  bumpNpcPos: (avatarId, x, z, rotationY) => set((s) => ({
    npcList: s.npcList.map((n) => (n.avatarId === avatarId
      ? { ...n, post: [x, (Array.isArray(n.post) ? n.post[1] : 0) || 0, z], rotationY: rotationY ?? n.rotationY }
      : n)),
  })),
  // Building-grid brush rotation. Snapped to 90° because a city grid wants
  // alignment — a paste that lands 7° off reads as a mistake, not as variety.
  // What the last click on the grid actually did. Three rounds of "it doesn't
  // work" went by without knowing whether the click reached the handler, whether
  // it had a brush, or whether the paste landed — so the tool now says so.
  blockLast: null,          // { gx, gz, ok, why }
  setBlockLast: (blockLast) => set({ blockLast }),

  blockRot: 0,
  turnBlock: (dir) => set((s) => ({ blockRot: s.blockRot + dir * (Math.PI / 2) })),

  // ── Room objects (⚙ → Interior avatar / Record sleeves) ─────────────────
  // Loaded from world_room_objects and edited IN PLACE here. Buttons mutate this
  // state directly rather than dispatching synthetic key events: the interiors
  // are first-person rooms whose camera already listens for arrows and WASD on
  // window, so a synthetic ArrowLeft moved the camera and the object together
  // and it read as "nothing moves".
  //
  // `dirty` is which ids have unsaved edits — that is what the Save button acts
  // on, and what makes leaving the room with pending changes noticeable.
  worldObjects: [],
  worldObjectSel: null,
  worldObjectDirty: [],
  setWorldObjects: (worldObjects) => set({ worldObjects, worldObjectDirty: [] }),
  // Load ONE (zone, kind) slice, leaving every other slice alone.
  //
  // Four editors share this array — street residents, interior residents,
  // record sleeves, street buildings — and each used to REPLACE it on load.
  // Whichever resolved last silently wiped the others, so opening the building
  // grid made placed residents disappear until a reload put them back. Replacing
  // only the rows you actually loaded is the difference between one shared list
  // and four editors fighting over it.
  loadWorldObjectSlice: (zoneId, kind, rows) => set((s) => ({
    worldObjects: [
      ...s.worldObjects.filter((o) => !(o.zone_id === zoneId && o.kind === kind)),
      ...rows,
    ],
    worldObjectDirty: s.worldObjectDirty.filter((id) => {
      const o = s.worldObjects.find((x) => x.object_id === id);
      return o && !(o.zone_id === zoneId && o.kind === kind);
    }),
  })),
  setWorldObjectSel: (worldObjectSel) => set({ worldObjectSel }),

  // ── Portals (AdminPanel → "Portals") ──────────────────────────────────────
  // Which portal the panel is editing, and a counter the panel bumps after a
  // save so the renderer refetches. A counter rather than pushing rows into the
  // store because portals are read by exactly one component — the store would be
  // a second copy of the truth for no reader.
  // Live per-room fit multiplier (CameraPanel). Not persisted: it exists to
  // arrive at a number you then bake into `fitTo`, the same way orbit and spawn
  // are tuned and copied. Keeping it out of localStorage means a tuning session
  // you walked away from cannot quietly become the room's real size.
  roomFitTune: {},
  setRoomFitTune: (roomId, v) => set((s) => ({ roomFitTune: { ...s.roomFitTune, [roomId]: v } })),
  // Live floor-height tuning, same deal as the fit: a value you arrive at by
  // standing in the room, then save. Needed because auto-grounding drops a
  // model's lowest surface to your feet, and "lowest surface" is not "the level
  // you arrive on" for anything with more than one storey — the metro grounds on
  // its upper concourse while the platform you actually want is below it.
  roomYTune: {},
  setRoomYTune: (roomId, v) => set((s) => ({ roomYTune: { ...s.roomYTune, [roomId]: v } })),

  // SAVED ROOM SETTINGS — fit, spawn and walk bounds, as data rather than code.
  //
  // These three were the last things in the world still tuned by copying a line
  // out of a panel and pasting it into rooms.js, which makes resizing a room a
  // DEPLOY. Everything else that gets arranged in-world — portals, venue
  // anchors, residents, sleeves — has been a row for a while; this brings the
  // room itself in line.
  //
  // Stored as world_objects (kind 'room_config', zone_id = the room), so it
  // needs no migration: `kind` is a free column and `data` carries the shape.
  // rooms.js stays the DEFAULT — a room with no row behaves exactly as authored,
  // and deleting the row restores it.
  roomConfigs: {},
  setRoomConfig: (roomId, cfg) => set((s) => ({ roomConfigs: { ...s.roomConfigs, [roomId]: cfg } })),

  portalSel: null,
  setPortalSel: (portalSel) => set({ portalSel }),
  portalVersion: 0,
  bumpPortals: () => set((s) => ({ portalVersion: s.portalVersion + 1 })),

  // HOLES IN THE GROUND, so a station sinks into it instead of sitting on it.
  //
  // Published by Portals (a portal with `data.pit`), consumed by CityBox, which
  // builds the outer ground as a shape with these subtracted. Kept in the store
  // rather than fetched twice because the portals are already loaded, and a
  // second fetch is a second chance for the ground and the station to disagree
  // about where the hole is.
  groundPits: [],
  setGroundPits: (groundPits) => set((s) => {
    const a = s.groundPits; const b = groundPits || [];
    // COMPARED ON EVERY FIELD, not a hand-picked few.
    //
    // This guard used to check x, z and `r`. When pits became rectangles the
    // radius stopped existing, so `p.r === b[i].r` compared undefined to
    // undefined, came out TRUE, and the guard concluded nothing had changed
    // every time only the width or depth moved. The size controls saved
    // correctly, reloaded correctly, and then hit a store that refused the
    // update — which looks exactly like a dead button.
    //
    // Serialising is a few microseconds on a handful of pits and cannot fall out
    // of step with the shape of the data again.
    if (JSON.stringify(a) === JSON.stringify(b)) return s;
    return { groundPits: b };
  }),
  markWorldObjectSaved: (id) => set((s) => ({ worldObjectDirty: s.worldObjectDirty.filter((n) => n !== id) })),

  // Local id for anything not yet in the DB, so an unsaved object still has a
  // stable identity to select and edit.
  addWorldObject: (obj) => set((s) => {
    const id = obj.object_id ?? `new:${Date.now()}:${s.worldObjects.length}`;
    const next = { ...obj, object_id: id };
    return { worldObjects: [...s.worldObjects, next], worldObjectSel: id, worldObjectDirty: [...s.worldObjectDirty, id] };
  }),
  removeWorldObject: (id) => set((s) => ({
    worldObjects: s.worldObjects.filter((o) => o.object_id !== id),
    worldObjectSel: s.worldObjectSel === id ? null : s.worldObjectSel,
    worldObjectDirty: s.worldObjectDirty.filter((n) => n !== id),
  })),
  // One mutator for every edit: the panel says what changed, not how to store it.
  patchWorldObject: (id, patch) => set((s) => ({
    worldObjects: s.worldObjects.map((o) => (o.object_id === id ? { ...o, ...patch } : o)),
    worldObjectDirty: s.worldObjectDirty.includes(id) ? s.worldObjectDirty : [...s.worldObjectDirty, id],
  })),

  npcEdit: { slug: null },
  setNpcEdit: (patch) => set((s) => ({ npcEdit: { ...s.npcEdit, ...patch } })),

  // Which admin tool is active (one at a time): null | 'cars' | 'guard' |
  // 'performer' | 'place' | 'routes' | 'cam' | 'bb'. The single AdminPanel hub
  // drives this; every tuning panel / in-scene edit mode reads it. Seeded from
  // the legacy ?<key>=edit params so old deep-links still open the right tool.
  adminTool: (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('cars') === 'edit') return 'cars';
      if (p.get('guard') === 'edit') return 'guard';
      if (p.get('place') === 'edit') return 'place';
      if (p.get('routes') === 'edit') return 'routes';
      if (p.get('cam') === 'edit') return 'cam';
      if (p.get('bb') === 'edit') return 'bb';
    } catch { /* SSR */ }
    return null;
  })(),
  setAdminTool: (adminTool) => set((s) => ({ adminTool: s.adminTool === adminTool ? null : adminTool })),

  // BUILD (PLAN) VIEW — straight down, orthographic, no fog or time of day.
  //
  // Deliberately NOT an `adminTool`. Those are mutually exclusive because each
  // one owns the click, and only one thing can own a click; this owns the
  // CAMERA, which every one of them still needs. It is a lens you look through
  // while using a tool, so it has to compose with the building grid rather than
  // replace it. ?plan=1 deep-links it.
  planView: (() => {
    try { return new URLSearchParams(window.location.search).get('plan') === '1'; }
    catch { return false; }
  })(),
  setPlanView: (planView) => set({ planView }),

  // Avatar ids this viewer follows / owns — the map pins the ones also present
  // as residents (with a headshot marker + fast-travel). Owned = "mine," shown
  // distinct from followed.
  following: [],
  setFollowing: (following) => set({ following }),
  owned: [],
  setOwned: (owned) => set({ owned }),

  // Show/hide the other-people markers (owned + followed avatar headshot pins) on
  // the HUD minimap. Owning a lot of avatars clouds the map, so this is a toggle
  // (persisted). Off = only the venue dots show.
  showPeople: (() => { try { return localStorage.getItem('showPeople') !== '0'; } catch { return true; } })(),
  toggleShowPeople: () => set((s) => {
    const showPeople = !s.showPeople;
    try { localStorage.setItem('showPeople', showPeople ? '1' : '0'); } catch { /* private mode */ }
    return { showPeople };
  }),

  // Concert-hall performer tuning (height + feet), live-adjustable in-scene via
  // PerformerPanel. Video and still frame differently (video = landscape with
  // margins), so seed per-source; ?perfh/?perfy still override, then the panel drives it.
  perfTune: (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const q = (k, d) => { const v = parseFloat(p.get(k)); return Number.isFinite(v) ? v : d; };
      const isVideo = !!p.get('perfvideo');
      return { h: q('perfh', isVideo ? 2.75 : 2.65), y: q('perfy', isVideo ? 1.98 : 1.74) };
    } catch { return { h: 2.65, y: 1.74 }; }
  })(),
  setPerfTune: (patch) => set((s) => ({ perfTune: { ...s.perfTune, ...patch } })),

  // Concert-hall security guard placement (offset from the concert-hall building
  // anchor), live-adjustable via GuardPanel (?guard=edit). Seeds from ?guard*.
  guardTune: (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const q = (k, d) => { const v = parseFloat(p.get(k)); return Number.isFinite(v) ? v : d; };
      return { x: q('guardx', -0.75), y: q('guardy', 0.1), z: q('guardz', 3.5), rot: q('guardrot', 0), h: q('guardh', 0.68) };
    } catch { return { x: -0.75, y: 0.1, z: 3.5, rot: 0, h: 0.68 }; }
  })(),
  setGuardTune: (patch) => set((s) => ({ guardTune: { ...s.guardTune, ...patch }, guardDirty: true })),
  // The saved row's id (null until it has been saved once), and whether the live
  // tune has drifted from it. Kept beside guardTune rather than in worldObjects
  // because the guard is ONE fixture, not a list — a whole selection model for a
  // single body would be ceremony.
  guardObjectId: null,
  guardDirty: false,
  // WHICH DOOR the panel is editing. The tune slice above is one body's worth of
  // numbers; this says whose. Everything else in the city renders from its saved
  // row — only the selected one follows the panel live.
  // The select screen. Opens for members on entry and closes when they pick —
  // guests have no avatars to choose between, so it never shows for them.
  charSelect: false,
  setCharSelect: (charSelect) => set({ charSelect }),
  guardRef: 'concert-hall',
  setGuardRef: (guardRef) => set({ guardRef, guardObjectId: null, guardDirty: false }),
  setGuardSaved: (guardObjectId) => set({ guardObjectId, guardDirty: false }),
  // Applying a saved row is not an edit; it must not light up the Save button.
  applyGuardTune: (tune, guardObjectId) => set({ guardTune: tune, guardObjectId, guardDirty: false }),

  // Radio DJ video-host cameo on the big billboards (dynamic moments only —
  // scheduled narrations stay audio-only, see useDjBillboard). phase mirrors the
  // website cameo machine; videoEl is the hidden <video> the Anam stream binds
  // to, which CityBillboards samples into a VideoTexture while live.
  djCameo: { phase: 'idle', hostName: null, videoEl: null },
  setDjCameo: (patch) => set((s) => ({ djCameo: { ...s.djCameo, ...patch } })),

  // Car size knob (?cars=edit). The cars (hero Lowrider + route traffic) were
  // authored at real-world meters, but the town is placed at a reduced scale to
  // match the small avatars — so cars need scaling down. One global scalar,
  // live-tunable and persisted to localStorage; bake the tuned value as CAR_SCALE_DEFAULT.
  carScale: readCarScale(),
  setCarScale: (v) => set(() => {
    const carScale = Math.max(0.05, Math.round(v * 1000) / 1000);
    try { localStorage.setItem('carscale', String(carScale)); } catch { /* private mode */ }
    return { carScale };
  })
}));
// ── TEST HOOK ────────────────────────────────────────────────────────────────
// The console needs a way in. Marks and seating are the first things authored
// content will drive, and until dialogue exists the only way to fire them is by
// hand — via `?mark=`, which costs a reload, and a reload drops you at the world
// entry and makes you walk back to whatever you were testing.
//
//   world.mark('wash_chair_1')      light it up, with its action
//   world.mark('chair_2', 'stool_1')
//   world.clear()
//   world.sit()  / world.stand()    skip the button
//   world.state()                   what the store thinks is going on
//
// Left in deliberately rather than gated to dev: it reads and sets state that
// the UI already exposes, so it grants nothing a determined visitor could not
// do anyway, and it is the difference between testing a scene in a second and
// reloading for every attempt.
if (typeof window !== 'undefined') {
  window.world = {
    store: useWorldStore,
    mark: (...targets) => useWorldStore.getState().setMarks(targets.flat().map((t) => ({ target: t }))),
    clear: () => useWorldStore.getState().setMarks([]),
    stand: () => useWorldStore.getState().standUp(),
    // Drive the blocking without sitting through the cue. `world.stage('sassy_la')`
    // with no mark sends her home, which is the same call the Done button makes.
    stage: (slug, mark) => useWorldStore.getState().stageTo(slug, mark || null),
    // Pretend to be on another rung: world.access('everyone') to get turned away,
    // world.access() to see where you stand. Testing the refusal otherwise means
    // holding a second account with no subscription, which is the kind of setup
    // cost that stops a path from being tested at all. It only moves the CLIENT's
    // opinion — the API still answers to your real token, which is the honest
    // demonstration that this gate is presentation and not security.
    // Same toggle as the F key, for when you are stuck under the floor and would
    // rather not hunt for focus in the canvas. world.where() prints the camera,
    // which is the number a spawn is authored from.
    free: (on) => {
      const s = useWorldStore.getState();
      s.setFreeCam(on === undefined ? !s.freeCam : !!on);
      return useWorldStore.getState().freeCam;
    },
    // world.speed() — one line a second: how far you actually moved, how often
    // the floor probe found ground, and the frame rate. Walk in and out of
    // whatever feels wrong and watch which number changes.
    speed: () => {
      useWorldStore.getState().toggleSpeedMeter();
      const on = useWorldStore.getState().speedMeter;
      console.log(`[speed] meter ${on ? 'ON — walk around; u/s should not change between places' : 'OFF'}`);
      return on;
    },
    // WHERE AM I — and the line to paste into rooms.js.
    //
    // Built because the alternative is what just happened: a spawn derived from
    // model bounds and a plan of where the platform ought to be, which landed
    // inside a row of benches. Stand where the spawn belongs, look where it
    // should look, read it off.
    where: () => {
      const r = (v) => Math.round(v * 100) / 100;
      const { px, py, pz, fx, fz } = camBasis;
      const line = `spawn: { pos: [${r(px)}, ${r(py)}, ${r(pz)}], look: [${r(px + fx * 6)}, ${r(py - 0.05)}, ${r(pz + fz * 6)}] },`;
      console.log(`%c${line}`, 'font: 600 13px monospace; color:#9dff7d');
      return line;
    },
    // ── VR: point the picture at the lenses, not at the middle of the half ──
    //
    //   world.lens(64, 154.3)   lens separation mm, screen WIDTH mm (landscape)
    //   world.lens()            what is currently applied
    //   world.lens(0)           back to a plain 50/50 split
    //
    // Two measurements, both takeable with a ruler: the distance between the
    // centres of the holder's lenses, and the long edge of the phone's glass.
    // Screen width cannot be read from JS — devicePixelRatio says nothing about
    // physical size — so it is asked for rather than assumed.
    //
    //   iPhone 14 Pro Max  154.3     iPhone 15/16 Pro   146.7
    //   iPhone SE (3rd)    104.1     Pixel 8            141.6
    //
    // The half's centre sits at screenW/4 from the edge; the lens sits at
    // (screenW - lensSep)/2. The gap between them is the error, and it is the
    // same on both sides, so one number moves both eyes inward.
    lens: (lensSepMm, screenWidthMm) => {
      if (lensSepMm === undefined) return useWorldStore.getState().eyeShift;
      if (!lensSepMm) return useWorldStore.getState().setEyeShift(0);
      if (!screenWidthMm) {
        console.warn('[vr] world.lens(lensSepMm, screenWidthMm) — measure the long edge of the glass, in mm');
        return useWorldStore.getState().eyeShift;
      }
      // Landscape: the CSS viewport's long side spans the screen's long side.
      const cssW = Math.max(window.innerWidth, window.innerHeight);
      const mm = screenWidthMm / 4 - lensSepMm / 2;
      const px = useWorldStore.getState().setEyeShift(mm * (cssW / screenWidthMm));
      console.log(`[vr] half is ${(screenWidthMm / 2).toFixed(1)}mm wide, lenses ${lensSepMm}mm apart → shifting each eye ${mm.toFixed(1)}mm (${px}px) inward`);
      return px;
    },
    access: (level) => {
      if (!level) return useWorldStore.getState().accessLevel;
      useWorldStore.getState().setAccessLevel(level);
      return level;
    },
    state: () => {
      const s = useWorldStore.getState();
      return {
        interior: s.interior,
        marks: s.marks,
        staging: s.staging,
        seated: s.seated ? { target: s.seated.target, phase: s.seated.phase } : null,
      };
    },
  };
}
